import React, { useEffect, useState, useMemo } from 'react';
import {
  Card, Button, Table, Tag, Space, Typography, Input, Modal, message, Popconfirm, Empty, Select, Tooltip,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, CopyOutlined, ReloadOutlined,
  TeamOutlined, DatabaseOutlined, LockOutlined,
} from '@ant-design/icons';

import { listGroups, deleteGroup, createGroup, getGroup } from '../services/dataAccessGroupsService';
import DataAccessGroupModal from '../components/DataAccessGroupModal';
import { useTheme } from '../contexts/ThemeContext';

const { Title, Text } = Typography;

export default function DataAccessGroups() {
  const { themeConfig } = useTheme();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [editingId, setEditingId] = useState(null);  // null = closed, 0 = new, id = edit
  const [reassignModal, setReassignModal] = useState(null); // { group } or null
  const [reassignTarget, setReassignTarget] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const data = await listGroups();
      setGroups(Array.isArray(data) ? data : []);
    } catch (e) {
      message.error(e.message || 'Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) =>
      (g.name && g.name.toLowerCase().includes(q)) ||
      (g.description && g.description.toLowerCase().includes(q)) ||
      (g.slug && g.slug.toLowerCase().includes(q)),
    );
  }, [groups, searchText]);

  const handleDelete = async (g) => {
    if (g.member_count > 0) {
      setReassignTarget(null);
      setReassignModal({ group: g });
      return;
    }
    try {
      await deleteGroup(g.id);
      message.success(`Group "${g.name}" deleted.`);
      reload();
    } catch (e) {
      message.error(e.message || 'Delete failed');
    }
  };

  const handleConfirmDelete = async () => {
    const g = reassignModal?.group;
    if (!g) return;
    try {
      await deleteGroup(g.id, {
        reassignToGroupId: reassignTarget || null,
        fallbackToCustom: !reassignTarget,
      });
      message.success(`Group "${g.name}" deleted. ${g.member_count} member(s) ${reassignTarget ? 'reassigned' : 'set to Custom'}.`);
      setReassignModal(null);
      setReassignTarget(null);
      reload();
    } catch (e) {
      message.error(e.message || 'Delete failed');
    }
  };

  const handleDuplicate = async (g) => {
    try {
      const detail = await getGroup(g.id);
      const copy = await createGroup({
        name: `${detail.name} (copy)`,
        description: detail.description,
        denials: detail.denials,
      });
      message.success(`Created "${copy.name}".`);
      reload();
    } catch (e) {
      message.error(e.message || 'Duplicate failed');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (name, g) => (
        <Space>
          <Tag color={g.is_system ? 'purple' : 'blue'} icon={g.is_system ? <LockOutlined /> : <DatabaseOutlined />}>
            {name}
          </Tag>
          {g.is_system && <Tooltip title="Built-in group"><Text type="secondary" style={{ fontSize: 11 }}>system</Text></Tooltip>}
        </Space>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      render: (d) => <Text type="secondary" style={{ fontSize: 13 }}>{d || <Text type="secondary" italic>—</Text>}</Text>,
    },
    {
      title: 'Members',
      dataIndex: 'member_count',
      width: 110,
      align: 'center',
      render: (n) => (
        <Space size={4}>
          <TeamOutlined style={{ color: themeConfig.primary }} />
          <Text strong>{n || 0}</Text>
        </Space>
      ),
    },
    {
      title: 'Denials (Schemas / Views)',
      width: 200,
      align: 'center',
      render: (_, g) => (
        <Space>
          <Tag color={g.denial_schema_count > 0 ? 'red' : 'default'}>{g.denial_schema_count} sch</Tag>
          <Tag color={g.denial_table_count > 0 ? 'orange' : 'default'}>{g.denial_table_count} views</Tag>
        </Space>
      ),
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      width: 160,
      render: (d) => d ? new Date(d).toLocaleString() : '—',
    },
    {
      title: 'Actions',
      width: 200,
      align: 'right',
      render: (_, g) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => setEditingId(g.id)}>
            Edit
          </Button>
          <Button size="small" icon={<CopyOutlined />} onClick={() => handleDuplicate(g)}>Duplicate</Button>
          <Popconfirm
            title={g.member_count > 0
              ? `Delete and reassign ${g.member_count} member(s)?`
              : `Delete "${g.name}"?`}
            onConfirm={() => handleDelete(g)}
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24, background: themeConfig.contentBg, minHeight: '100vh' }}>
      <div style={{ marginBottom: 20 }}>
        <Title level={3} style={{ margin: 0, color: themeConfig.primary, fontWeight: 'bold', letterSpacing: 1 }}>
          DATA ACCESS GROUPS
        </Title>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Manage reusable schema/view denial sets. Users assigned to a group inherit its denials.
        </Text>
      </div>

      <Card
        style={{ borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
        title={
          <Space>
            <DatabaseOutlined style={{ color: themeConfig.primary }} />
            <span style={{ fontWeight: 600 }}>Groups</span>
            <Tag color={themeConfig.primary}>{filtered.length}</Tag>
          </Space>
        }
        extra={
          <Space>
            <Input.Search
              placeholder="Search groups…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{ width: 240 }}
            />
            <Button icon={<ReloadOutlined />} onClick={reload}>Refresh</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditingId(0)}>New Group</Button>
          </Space>
        }
      >
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={filtered}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No groups yet" /> }}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
        />
      </Card>

      <DataAccessGroupModal
        open={editingId !== null}
        groupId={editingId || null}
        onClose={() => setEditingId(null)}
        onSaved={reload}
      />

      <Modal
        open={!!reassignModal}
        title={reassignModal ? `Delete "${reassignModal.group.name}"` : ''}
        onCancel={() => setReassignModal(null)}
        onOk={handleConfirmDelete}
        okButtonProps={{ danger: true }}
        okText="Delete"
      >
        {reassignModal && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>
              This group has <Text strong>{reassignModal.group.member_count}</Text> member(s).
              Choose what to do with them:
            </Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Set members to Custom (no group)"
              allowClear
              value={reassignTarget}
              onChange={setReassignTarget}
              options={groups
                .filter((g) => g.id !== reassignModal.group.id)
                .map((g) => ({ value: g.id, label: `Move to: ${g.name}` }))}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Leave blank to mark every member as Custom (no group) — their existing override denials, if any, are preserved.
            </Text>
          </Space>
        )}
      </Modal>
    </div>
  );
}
